import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Star } from "lucide-react";

const EMPTY = { name: "", care_home: "", quote: "", rating: 5, featured: true };

function TestimonialDialog({ open, onOpenChange, initial, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ ...EMPTY, ...(initial || {}) }); }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      if (initial?.id) await api.put(`/admin/testimonials/${initial.id}`, f); else await api.post("/admin/testimonials", f);
      toast.success(initial?.id ? "Testimonial updated" : "Testimonial added"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="testimonial-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading text-brand">{initial?.id ? "Edit testimonial" : "Add testimonial"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Quote</Label><Textarea data-testid="testimonial-quote" required rows={4} minLength={10} value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input data-testid="testimonial-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Sarah, Registered Manager" /></div>
            <div><Label>Care home / source</Label><Input data-testid="testimonial-home" value={f.care_home || ""} onChange={(e) => setF({ ...f, care_home: e.target.value })} placeholder="e.g. Google review" /></div>
          </div>
          <div className="flex items-center justify-between">
            <div><Label>Rating</Label>
              <div className="flex gap-1 mt-1" data-testid="testimonial-rating">{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" onClick={() => setF({ ...f, rating: n })}><Star className={`h-5 w-5 ${n <= f.rating ? "fill-coral text-coral" : "text-cream-deep"}`} /></button>)}</div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="testimonial-featured" checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} />Show on home page</label>
          </div>
          <Button type="submit" data-testid="testimonial-save" disabled={busy} className="rounded-full bg-brand hover:bg-brand-hover text-white">{busy ? "Saving…" : "Save"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items]; [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    try { await api.put("/admin/testimonials/reorder", next.map((t) => t.id)); } catch { toast.error("Reorder failed"); load(); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete the testimonial from ${t.name}?`)) return;
    try { await api.delete(`/admin/testimonials/${t.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggle = async (t) => {
    try { await api.put(`/admin/testimonials/${t.id}`, { ...t, featured: !t.featured }); load(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-sm text-muted-foreground">The first three testimonials marked "Show on home page" appear on the website, in this order.</p>
        <Button size="sm" data-testid="testimonial-add-btn" onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-coral hover:bg-coral-hover text-white"><Plus className="h-4 w-4 mr-1" />Add testimonial</Button>
      </div>
      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-muted-foreground">No testimonials yet.</p>}
        {items.map((t, i) => (
          <div key={t.id} data-testid={`testimonial-row-${i}`} className={`bg-cream-card border rounded-xl p-4 flex gap-4 ${t.featured && i < 3 ? "border-coral/40" : "border-cream-deep"}`}>
            <div className="flex flex-col gap-1 shrink-0">
              <Button size="icon" variant="ghost" data-testid={`testimonial-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} className="h-7 w-7"><ArrowUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`testimonial-down-${i}`} disabled={i === items.length - 1} onClick={() => move(i, 1)} className="h-7 w-7"><ArrowDown className="h-4 w-4" /></Button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex gap-0.5 mb-1">{Array.from({ length: t.rating }).map((_, k) => <Star key={`star-${k}`} className="h-3.5 w-3.5 fill-coral text-coral" />)}</div>
              <p className="text-sm leading-relaxed">“{t.quote}”</p>
              <div className="text-xs text-muted-foreground mt-2"><b className="text-brand">{t.name}</b>{t.care_home ? ` · ${t.care_home}` : ""}{t.featured && i < 3 && <span className="ml-2 text-coral font-medium">· On home page</span>}</div>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <label className="flex items-center gap-2 text-xs"><Switch data-testid={`testimonial-toggle-${i}`} checked={t.featured} onCheckedChange={() => toggle(t)} />Show</label>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" data-testid={`testimonial-edit-${i}`} onClick={() => { setEditing(t); setOpen(true); }} className="h-8 rounded-full"><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" data-testid={`testimonial-delete-${i}`} onClick={() => remove(t)} className="h-8 rounded-full text-rag-red"><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <TestimonialDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={load} />
    </div>
  );
}
